import { useMemo } from 'react';

import { UploadFile } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSanitizedRouteParams } from 'src/hooks/useSanitizedRouteParams';

export const routesMap = {
  landing: '/',
  auth0Callback: '/auth0-callback',
  adminClients: '/admin-clients',
  adminClientsNew: '/admin-clients/new',
  adminClientsDetails: '/admin-clients/:id',
  adminUsers: '/admin-users',
  adminUsersNew: '/admin-users/new',
  adminUsersDetails: '/admin-users/:username',
  users: '/users',
  usersNew: '/users/new',
  usersDetails: '/users/:username',
  settings: '/settings',
  reports: '/reports',
  ticketLogs: '/ticket-logs',
  paymentLogs: '/payment-logs',
  accessLogs: '/access-logs',
  lists: '/lists',
  listNew: '/lists/new',
  listDetails: '/lists/:id',
  listDetailsAddPeople: '/lists/:id/add-people',
  people: '/people',
  peopleDetails: '/people/:id/:tab?',
  peopleDetailsEdit: '/people/:id/edit',
  peopleNew: '/people/new',
  peopleBulkImport: '/people/bulk-import',
  peopleImportProcess: '/people/import-process/:jobId',
  venuesAndEvents: '/venues-and-events/:tab',
  venuesAndEventsNew: '/venues-and-events/venues/new',
  venuesAndEventsEdit: '/venues-and-events/venues/:id/edit',
  devices: '/sensors-controllers',
  devicesDetails: '/sensors-controllers/:type/:id/:tab',
  devicesDetailsLogs: '/sensors-controllers/:type/:id/logs',
  deviceNew: '/sensors-controllers/new',
  groups: '/groups',
  groupDetails: '/groups/:id/:tab',
  groupNew: '/groups/new',
  events: '/events',
  account: '/account',
  preferences: '/preferences',
  kiosk: '/kiosk',
  kioskDetails: '/kiosk/:tab?',
  dayPasses: '/day-passes',
  dayPassesNew: '/day-passes/new',
  dayPassesDetails: '/day-passes/:id',
  connections: '/connections',
  connectionsNew: '/connections/new',
  connectionsDetails: '/connections/:id',
  sites: '/sites',
  site: '/sites/:id',
  siteDetails: '/sites/:id/:tab',
  siteNew: '/sites/new',
  campaign: '/sites/:id/campaigns/:campaignId',
  campaignDetails: '/sites/:id/campaigns/:campaignId/:tab',
  campaignNew: '/sites/:id/campaigns/new',
  emailTemplates: '/email-templates',
  emailTemplateNew: '/email-templates/new',
  emailTemplateDetails: '/email-templates/:id',
  ticketGroups: '/ticket-groups',
  ticketGroupsNew: '/ticket-groups/new',
  ticketGroupsEdit: '/ticket-groups/:id/edit',
  noPermission: '/no-permission',
  errorPage: '/error/:status',
  login: '/login',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  verifyEmail: '/verify-email',
  assets: '/assets',
  support: '/support',
  accountSettings: '/account-settings',
  notifications: '/notifications',
};

type RoutesMapKeys = keyof typeof routesMap;

export const useAppNavigation = () => {
  const navigate = useNavigate();
  const { id } = useSanitizedRouteParams();

  const navigateTo = useMemo(
    () => ({
      landing: () => navigate(routesMap.landing),
      auth0Callback: () => navigate(routesMap.auth0Callback),
      adminClients: () => navigate(routesMap.adminClients),
      adminClientsNew: () => navigate(routesMap.adminClientsNew),
      adminClientsDetails: (clientId?: string | null) => {
        if (clientId) {
          return navigate(routesMap.adminClientsDetails.replace(':id', clientId));
        }
        return navigate(routesMap.adminClients);
      },
      adminUsers: () => navigate(routesMap.adminUsers),
      adminUsersNew: () => navigate(routesMap.adminUsersNew),
      adminUsersDetails: (username?: string | null) => {
        if (username) {
          return navigate(routesMap.adminUsersDetails.replace(':username', username));
        }
        return navigate(routesMap.adminUsers);
      },
      users: () => navigate(routesMap.users),
      usersNew: () => navigate(routesMap.usersNew),
      usersDetails: (username?: string | null) => {
        if (username) {
          return navigate(routesMap.usersDetails.replace(':username', username));
        }
        return navigate(routesMap.users);
      },
      settings: () => navigate(routesMap.settings),
      reports: () => navigate(routesMap.reports),
      ticketLogs: () => navigate(routesMap.ticketLogs),
      paymentLogs: () => navigate(routesMap.paymentLogs),
      accessLogs: () => navigate(routesMap.accessLogs),
      lists: () => navigate(routesMap.lists),
      listDetails: (id?: string) => {
        if (id) {
          return navigate(routesMap.listDetails.replace(':id', id));
        }
        return navigate(routesMap.lists);
      },
      listDetailsAddPeople: (id?: string) => {
        if (id) {
          return navigate(routesMap.listDetailsAddPeople.replace(':id', id));
        }
        return navigate(routesMap.lists);
      },
      listNew: () => navigate(routesMap.listNew),
      people: () => navigate(routesMap.people),
      peopleDetails: (id?: string, tab?: string) => {
        if (id) {
          return navigate(routesMap.peopleDetails.replace(':id', id).replace(':tab?', tab || 'information'));
        }
        return navigate(routesMap.people);
      },
      peopleDetailsEdit: (id?: string) => {
        if (id) {
          return navigate(routesMap.peopleDetailsEdit.replace(':id', id));
        }
        return navigate(routesMap.people);
      },
      peopleNew: (queryParams?: Record<string, string>) =>
        navigate(`${routesMap.peopleNew}?${new URLSearchParams(queryParams).toString()}`),
      peopleBulkImport: ({
        listId,
        fileList,
        includeHeader,
      }: {
        listId?: string;
        fileList: UploadFile[];
        includeHeader: boolean;
      }) =>
        navigate(routesMap.peopleBulkImport, {
          state: {
            listId,
            fileList,
            includeHeader,
          },
        }),
      peopleImportProcess: ({
        jobId,
        numberOfRows,
        fileName,
      }: {
        jobId: string;
        numberOfRows: number;
        fileName: string;
      }) =>
        navigate(routesMap.peopleImportProcess.replace(':jobId', jobId), {
          state: {
            numberOfRows,
            fileName,
          },
        }),
      venuesAndEvents: (tab?: string) => navigate(routesMap.venuesAndEvents.replace(':tab', tab || 'venues')),
      venuesAndEventsNew: () => navigate(routesMap.venuesAndEventsNew),
      venuesAndEventsEdit: (id?: string) => {
        if (id) {
          return navigate(routesMap.venuesAndEventsEdit.replace(':id', id));
        }
        return navigate(routesMap.venuesAndEvents);
      },
      devices: () => navigate(routesMap.devices),
      sensorsControllers: () => navigate(routesMap.devices),
      groups: () => navigate(routesMap.groups),
      groupDetails: (id?: string, tab?: string) =>
        navigate(routesMap.groupDetails.replace(':id', id || '').replace(':tab', tab || 'settings')),
      groupNew: () => navigate(routesMap.groupNew),
      devicesDetails: (id?: string, type?: string, tab?: string) =>
        navigate(
          routesMap.devicesDetails
            .replace(':type', type || '')
            .replace(':id', id || '')
            .replace(':tab', tab || ''),
        ),
      devicesDetailsLogs: (id?: string, type?: string) =>
        navigate(routesMap.devicesDetailsLogs.replace(':type', type || '').replace(':id', id || '')),
      deviceNew: () => navigate(routesMap.deviceNew),
      events: () => navigate(routesMap.events),
      account: () => navigate(routesMap.account),
      preferences: () => navigate(routesMap.preferences),
      kiosk: () => navigate(routesMap.kiosk),
      kioskDetails: (tab?: string) => navigate(routesMap.kioskDetails.replace(':tab', tab || 'settings')),
      connections: () => navigate(routesMap.connections),
      addConnection: () => navigate(routesMap.connectionsNew),
      connectionsDetails: (id?: string) => {
        if (id) {
          return navigate(routesMap.connectionsDetails.replace(':id', id));
        }
        return navigate(routesMap.connections);
      },
      sites: () => navigate(routesMap.sites),
      site: (id?: string) => {
        if (id) {
          return navigate(routesMap.site.replace(':id', id));
        }
        return navigate(routesMap.sites);
      },
      siteDetails: (id?: string, tab?: string) => {
        if (id) {
          return navigate(routesMap.siteDetails.replace(':id', id).replace(':tab', tab || 'settings'));
        }
        return navigate(routesMap.siteDetails.replace(':id', id || '').replace(':tab', tab || 'settings'));
      },
      siteNew: () => navigate(routesMap.siteNew),
      campaign: (id?: string, campaignId?: string) => {
        if (id && campaignId) {
          return navigate(routesMap.campaign.replace(':id', id).replace(':campaignId', campaignId));
        }
        return navigate(routesMap.campaign.replace(':id', id || '').replace(':campaignId', campaignId || ''));
      },
      campaignDetails: (id?: string, campaignId?: string, tab?: string) =>
        navigate(
          routesMap.campaignDetails
            .replace(':id', id || '')
            .replace(':campaignId', campaignId || '')
            .replace(':tab', tab || 'settings'),
        ),
      campaignNew: (id?: string) => {
        if (id) {
          return navigate(routesMap.campaignNew.replace(':id', id));
        }
        return navigate(routesMap.campaignNew.replace(':id', id || ''));
      },
      emailTemplates: () => navigate(routesMap.emailTemplates),
      emailTemplateNew: (type?: string) =>
        navigate(routesMap.emailTemplateNew, {
          state: { type },
        }),
      emailTemplateDetails: (id?: string) => {
        if (id) {
          return navigate(routesMap.emailTemplateDetails.replace(':id', id));
        }
        return navigate(routesMap.emailTemplates);
      },
      dayPasses: () => navigate(routesMap.dayPasses),
      dayPassesNew: () => navigate(routesMap.dayPassesNew),
      dayPassesDetails: (id?: string) => {
        if (id) {
          return navigate(routesMap.dayPassesDetails.replace(':id', id));
        }
        return navigate(routesMap.dayPasses);
      },
      ticketGroups: () => navigate(routesMap.ticketGroups),
      ticketGroupsNew: () => navigate(routesMap.ticketGroupsNew),
      ticketGroupsEdit: (id?: string) => {
        if (id) {
          return navigate(routesMap.ticketGroupsEdit.replace(':id', id));
        }
        return navigate(routesMap.ticketGroups);
      },
      noPermission: () => navigate(routesMap.noPermission),
      errorPage: (status?: number, message?: string) =>
        navigate(routesMap.errorPage.replace(':status', status?.toString() || ''), {
          state: { status, message },
        }),
      assets: () => navigate(routesMap.assets),
      login: (queryParams?: Record<string, string>) => {
        const url = queryParams ? `${routesMap.login}?${new URLSearchParams(queryParams).toString()}` : routesMap.login;
        return navigate(url);
      },
      forgotPassword: () => navigate(routesMap.forgotPassword),
      resetPassword: () => navigate(routesMap.resetPassword),
      verifyEmail: () => navigate(routesMap.verifyEmail),
      support: () => navigate(routesMap.support),
      accountSettings: () => navigate(routesMap.accountSettings),
      notifications: () => navigate(routesMap.notifications),
    }),
    [id],
  );

  return { navigateTo, routesMap };
};

export type { RoutesMapKeys };
